import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, ListGroup, Badge, Alert, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import ErrorBoundary from 'components/common/ErrorBoundary';

const ListaSuscripcionesCliente = ({ idCliente, refreshKey }) => {
    const { token } = useToken();
    const [suscripciones, setSuscripciones] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [totalSuscripciones, setTotalSuscripciones] = useState(0);


    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const observer = useRef();
    const scrollContainerRef = useRef();

    const lastElementRef = useCallback(node => {
        if (cargando) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPageNumber(prevPageNumber => prevPageNumber + 1);
            }
        }, {
            root: scrollContainerRef.current,
            rootMargin: '300px', // Empezar a cargar 300px antes de llegar al fondo

        });

        if (node) observer.current.observe(node);
    }, [cargando, hasMore]);

    const getTable = (obj, tableName) => {
        if (!obj || typeof obj !== 'object') return [];
        return obj[tableName] || obj[tableName.toLowerCase()] || [];
    };

    const obtenerSuscripcionesCliente = async () => {
        if (!idCliente) return;

        setCargando(true);
        try {
            const response = await fetch(`/api/Suscripciones/ObtenerListaSuscripciones`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idCliente: idCliente.toString(),
                    pageNumber: pageNumber,
                    pageSize: 10
                })
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {
                    const ObjDetalles = JSON.parse(data.detalles);

                    const detallesTotales = getTable(ObjDetalles, 'Table1');
                    if (detallesTotales.length > 0) {
                        setTotalSuscripciones(detallesTotales[0].GlobalCount);
                    }

                    const detalles = getTable(ObjDetalles, 'Table2');

                    if (detalles.length > 0) {
                        if (pageNumber === 1) {
                            setSuscripciones(detalles);
                        } else {
                            setSuscripciones(prev => [...prev, ...detalles]);
                        }

                        if (detalles.length < 10) {
                            setHasMore(false);
                        }
                    } else {
                        setHasMore(false);
                    }
                } else {
                    console.error("Error del backend:", data.vMessage);
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error en Fetch:', error);
            setHasMore(false);
        } finally {
            setCargando(false);
        }
    };

    // Si cambia el cliente o se fuerza el refresh, reiniciamos paginación
    useEffect(() => {
        if (idCliente) {
            setPageNumber(1);
            setSuscripciones([]);
            setHasMore(true);
        }
    }, [idCliente, refreshKey]);

    // Cuando cambia la página o se reinicia a 1, traemos datos
    useEffect(() => {
        if (idCliente && (pageNumber > 1 || suscripciones.length === 0)) {
            obtenerSuscripcionesCliente();
        }
    }, [pageNumber, idCliente, refreshKey]);

    const renderBadgeEstatus = (estado) => {
        const estatusConfig = {
            true: { bg: 'success', text: 'ACTIVA' },
            false: { bg: 'danger', text: 'CANCELADA' },
        };
        const config = estatusConfig[estado] || { bg: 'secondary', text: 'DESCONOCIDO' };
        return <Badge bg={config.bg}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    //esto es el componente de la lista
    return (
        <ErrorBoundary>
            <Card className="shadow-sm border-0">
                <Card.Header className="bg-transparent border-bottom pt-4 pb-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold">
                        <FontAwesomeIcon icon="list" className="me-2 text-primary" />
                        Suscripciones
                    </h5>
                    {totalSuscripciones > 0 && (
                        <Badge bg="primary" className="rounded-pill shadow-sm px-3 py-2" style={{ fontSize: '0.9rem' }}>
                            {totalSuscripciones} suscripciónes
                        </Badge>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {cargando && suscripciones.length === 0 ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : suscripciones.length > 0 ? (
                        <div
                            ref={scrollContainerRef}
                            style={{ maxHeight: '400px', overflowY: 'auto' }}
                        >
                            <ListGroup variant="flush">
                                {suscripciones.map((suscripcion, index) => {
                                    const isLast = index === suscripciones.length - 1;
                                    return (
                                        <ListGroup.Item
                                            key={suscripcion.idSuscripcion}
                                            ref={isLast ? lastElementRef : null}
                                            className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center p-3 gap-3"
                                        >
                                            {/* Columna Izquierda: ID, Título, Precio y Creación */}
                                            <div className="d-flex align-items-center" style={{ minWidth: '250px' }}>
                                                <Badge bg="light" text="dark" className="border me-3">
                                                    #{suscripcion.idSuscripcion}
                                                </Badge>
                                                <div>
                                                    <h6 className="mb-1 fw-bold text-dark">
                                                        {suscripcion.vNombreSuscripcion}
                                                    </h6>
                                                    <small className="text-muted d-block">
                                                        ${parseFloat((suscripcion.dPrecio ?? suscripcion.dTotal) || 0).toFixed(2)} {suscripcion.vMoneda || 'MXN'}
                                                        {suscripcion.dCreatedDate && (
                                                            <> &bull; <span className="mb-1 fw-bold text-dark">Creada</span> {formatDate(suscripcion.dCreatedDate)}</>
                                                        )}
                                                        {suscripcion.dProximoPago && (
                                                            <> &bull; <span className="mb-1 fw-bold text-dark">Próximo Pago</span> {formatDate(suscripcion.dProximoPago)}</>
                                                        )}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Columna Derecha: Estatus y Acción */}
                                            <div className="d-flex align-items-center gap-4 mt-2 mt-md-0">
                                                {renderBadgeEstatus(suscripcion.bIsActive)}

                                                <Link
                                                    to={`/suscripciones/${suscripcion.idSuscripcion}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                                >
                                                    Detalles <FontAwesomeIcon icon="external-link-alt" className="ms-1" />
                                                </Link>
                                            </div>
                                        </ListGroup.Item>
                                    );
                                })}
                                {cargando && suscripciones.length > 0 && (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" variant="primary" />
                                    </div>
                                )}
                            </ListGroup>
                        </div>
                    ) : (
                        <Alert variant="light" className="text-center py-4 mb-0 border-0">
                            <small className="text-muted">No hay suscripciones registradas para este cliente.</small>
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </ErrorBoundary>
    );
};

ListaSuscripcionesCliente.propTypes = {
    idCliente: PropTypes.string.isRequired,
    refreshKey: PropTypes.number,
};

export default ListaSuscripcionesCliente;
